import type { Ref } from 'vue';
import {
  computed,
} from 'vue';

import type { VRouterTab } from '@components/tabs/VRouterTabs/types';
import type { ITableFilterProps } from '@modules/tables/components/VTableFilter/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import {
  AFFILIATE_PROGRAM_CREATIVES_LIST_ROUTE_NAME,
  TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES,
} from '@modules/affiliate-program-creatives/constants';
import { FORMAT_MONTH_YEAR_DAY } from '@modules/core/composables/datetime/constants';
import { useI18n } from '@modules/i18n/composables';
import { ESmartFilterColumnType } from '@modules/smart-filters/enums';

export interface AffiliateProgramCreativesListPageComposable {
  tableTabs: Ref<VRouterTab[]>;
  filterOptions: Ref<ITableFilterProps>;
  columnOptions: ITableColumnsOptions;
}

export default function useAffiliateProgramCreativesListPage(
  type: Ref<string>,
): AffiliateProgramCreativesListPageComposable {
  const { getTranslation } = useI18n();

  const tableTabs = computed<VRouterTab[]>(() => [
    {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_LINK').value,
      route: {
        name: AFFILIATE_PROGRAM_CREATIVES_LIST_ROUTE_NAME,
        params: {
          type: 'link',
        },
      },
    },
    {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_JPEG').value,
      route: {
        name: AFFILIATE_PROGRAM_CREATIVES_LIST_ROUTE_NAME,
        params: {
          type: 'jpeg',
        },
      },
    },
    {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_PNG').value,
      route: {
        name: AFFILIATE_PROGRAM_CREATIVES_LIST_ROUTE_NAME,
        params: {
          type: 'png',
        },
      },
    },
    {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_GIF').value,
      route: {
        name: AFFILIATE_PROGRAM_CREATIVES_LIST_ROUTE_NAME,
        params: {
          type: 'gif',
        },
      },
    },
    {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_BANNERFLOW_GIF').value,
      route: {
        name: AFFILIATE_PROGRAM_CREATIVES_LIST_ROUTE_NAME,
        params: {
          type: 'bannerflow_gif',
        },
      },
    },
    {
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_CREATIVES, 'TAB_IFRAME').value,
      route: {
        name: AFFILIATE_PROGRAM_CREATIVES_LIST_ROUTE_NAME,
        params: {
          type: 'iframe',
        },
      },
    },
  ]);

  const filterOptions = computed<ITableFilterProps>(() => ({
    pathForFilter: 'api2/admin/creatives',
    actionForFilter: 'list',
    filterName: `creative_${type.value}`,
    byMerchant: true,
  }));

  const columnOptions: ITableColumnsOptions = {
    language: {
      type: ESmartFilterColumnType.CHECKBOX,
      localization: {
        translationPath: 'creative.list_locales.',
      },
    },
    status: {
      type: ESmartFilterColumnType.CHECKBOX,
      localization: {
        translationPathForTruthyValue: 'admin.partnership_program.creatives.settings.status_active',
        translationPathForFalsyValue: 'admin.partnership_program.creatives.settings.status_not_active',
      },
    },
    aasm_state: {
      type: ESmartFilterColumnType.CHECKBOX,
      localization: {
        translationPath: 'admin.partnership_program.creatives.settings.status_',
      },
    },
    at_beginning_show: {
      type: ESmartFilterColumnType.DATE,
      dateFormat: FORMAT_MONTH_YEAR_DAY,
    },
    at_end_show: {
      type: ESmartFilterColumnType.DATE,
      dateFormat: FORMAT_MONTH_YEAR_DAY,
    },
    merchant_name: {
      type: ESmartFilterColumnType.CHECKBOX,
    },
    id: {
      type: ESmartFilterColumnType.COMPARE,
    },
    title: {
      type: ESmartFilterColumnType.CHECKBOX,
      defaultMobilePreview: true,
    },
    advertising_company: {
      type: ESmartFilterColumnType.CHECKBOX,
    },
    created_at: {
      type: ESmartFilterColumnType.DATE,
    },
    resolution: {
      type: ESmartFilterColumnType.CHECKBOX,
    },
  };

  return {
    tableTabs,
    filterOptions,
    columnOptions,
  };
}
